import * as React from 'react';

import { ActivityIndicator, Pressable, View, WBIcon, IconNameType, WBText, TextProps, ViewProps } from '@/core';
import { getModifiedStyles } from '@/helpers';
import { getButtonStyle } from './utils';
import buttonStyles from './style';

export interface ButtonProps {
	/**
	 * Icon before text.
	 */
	icon?: IconNameType;
	/**
	 * Disables pressable actions on the component, and sets disabled style.
	 */
	isDisabled?: boolean;
	/**
	 * Sets a loader component in the middle of the button.
	 */
	isLoading?: boolean;
	/**
	 * Callback after user presses the button.
	 */
	onPress: () => void;
	/**
	 * Theme that sets shape of button.
	 */
	theme?: 'default' | 'circular';
	/**
	 * Type that sets background color and text color.
	 */
	type?: 'primary' | 'mint' | 'secondary' | 'tertiary' | 'quaternary' | 'white';
	/**
	 * An object to style the text of the component.
	 */
	textStyle?: TextProps['style'] & { color?: string };
	/**
	 * An object to style the outside container of the component.
	 */
	style?: ViewProps['style'];
}

export const WBButton: React.FC<ButtonProps> = ({
	children,
	textStyle,
	icon,
	isDisabled = false,
	isLoading = false,
	onPress,
	style,
	theme = 'default',
	type = 'quaternary',
}) => {
	const touchableProps = { disabled: isDisabled, onPress };
	const styles = getModifiedStyles(buttonStyles, { isDisabled }, theme);
	const buttonStyle = getButtonStyle(type);

	return (
		<Pressable {...touchableProps} activeOpacity={0.6} style={[styles.container, style]}>
			<View style={[{ backgroundColor: buttonStyle.backgroundColor }, styles.button]}>
				{!isLoading ? (
					<View style={styles.content}>
						{icon && (
							<WBIcon
								color={textStyle && textStyle.color ? textStyle.color : buttonStyle.color}
								name={icon}
								size={24}
							/>
						)}

						{children && (
							<WBText
								style={[
									styles.text,
									{ color: buttonStyle.color, paddingLeft: icon ? 8 : 0 },
									textStyle,
								]}
							>
								{children}
							</WBText>
						)}
					</View>
				) : (
					<View style={[styles.loadingIndicator, textStyle]}>
						<ActivityIndicator color={buttonStyle.color} />
					</View>
				)}
			</View>
		</Pressable>
	);
};
